import React, { useEffect, useState, useRef, useCallback } from 'react';
import AdminShell from '../../../components/admin/AdminShell';
import { marketingApi } from '../../../lib/api';
import { toast } from 'sonner';
import { Save, Eye, ArrowLeft } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useNavigate, useParams } from 'react-router-dom';

export default function TemplateEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    channel: 'sms',
    template_body: '',
  });
  const [sampleData, setSampleData] = useState(() => {
    // Try to load saved sample data from localStorage
    const saved = localStorage.getItem('template_sample_data');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {
          customer_name: 'John Doe',
          event_name: 'Saturday Night Live',
          date: 'Dec 28',
        };
      }
    }
    return {
      customer_name: 'John Doe',
      event_name: 'Saturday Night Live',
      date: 'Dec 28',
    };
  });
  const [preview, setPreview] = useState('');
  const [detectedVars, setDetectedVars] = useState([]);
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);

  // Save sample data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('template_sample_data', JSON.stringify(sampleData));
  }, [sampleData]);

  useEffect(() => {
    if (id) {
      marketingApi.getTemplate(id)
        .then(res => {
          setFormData({
            name: res.data.name || '',
            channel: res.data.channel || 'sms',
            template_body: res.data.template_body || '',
          });
        })
        .catch(() => toast.error('Failed to load template'))
        .finally(() => setLoading(false));
    }
  }, [id]);

  // Detect variables and update preview
  useEffect(() => {
    const regex = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    const matches = [...formData.template_body.matchAll(regex)];
    const vars = [...new Set(matches.map(m => m[1]))];
    setDetectedVars(vars);

    // Update preview
    let rendered = formData.template_body;
    vars.forEach(v => {
      const value = sampleData[v] || `{${v}}`;
      rendered = rendered.replace(new RegExp(`\\{${v}\\}`, 'g'), value);
    });
    setPreview(rendered);
  }, [formData.template_body, sampleData]);

  // Sync scroll between textarea and highlight overlay
  const handleScroll = () => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  // Warn on browser tab close / reload when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Helper to update form and mark dirty
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }, []);

  const handleSave = () => {
    if (!formData.name || !formData.template_body) {
      toast.error('Name and template body are required');
      return;
    }
    setSaving(true);
    const promise = id 
      ? marketingApi.updateTemplate(id, formData)
      : marketingApi.createTemplate(formData);
    
    promise
      .then(() => {
        setIsDirty(false);
        toast.success(`Template ${id ? 'updated' : 'created'}`);
        navigate('/admin/marketing/templates');
      })
      .catch(() => toast.error(`Failed to ${id ? 'update' : 'create'} template`))
      .finally(() => setSaving(false));
  };

  const handleBack = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Leave without saving?');
      if (!confirmed) return;
    }
    navigate('/admin/marketing/templates');
  };

  const renderHighlightedBody = () => {
    const regex = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    let lastIndex = 0;
    const parts = [];
    let match;

    while ((match = regex.exec(formData.template_body)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {formData.template_body.substring(lastIndex, match.index)}
          </span>
        );
      }
      // Add highlighted variable
      const varName = match[1];
      const isValid = sampleData.hasOwnProperty(varName);
      parts.push(
        <span
          key={`var-${match.index}`}
          className={`rounded px-1 py-0.5 border ${
            isValid
              ? 'bg-[rgba(212,168,67,0.14)] border-[rgba(212,168,67,0.28)] text-[#D4A843]'
              : 'bg-[rgba(255,77,77,0.14)] border-[rgba(255,77,77,0.28)] text-[#FFD7D7]'
          }`}
        >
          {match[0]}
        </span>
      );
      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < formData.template_body.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {formData.template_body.substring(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  if (loading) {
    return (
      <AdminShell title={id ? 'Edit Template' : 'New Template'}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell 
      title={id ? 'Edit Template' : 'New Template'}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleBack}
            className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className={`${isDirty ? 'bg-[#D4A843]' : 'bg-[#D4A843]/70'} text-black hover:brightness-110 gap-2`}
            data-testid="template-save-button"
          >
            <Save size={16} />
            {saving ? 'Saving...' : isDirty ? 'Save Changes*' : 'Save Template'}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Editor */}
        <div className="space-y-6">
          <Card className="bg-[#111111] border-[#2A2A2A]">
            <CardHeader>
              <CardTitle className="text-[#F2F2F2] text-base font-['EB_Garamond']">Template Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#B9B9B9] text-sm">Template Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g., Weekly Event Reminder"
                  className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#B9B9B9] text-sm">Channel</Label>
                <Select value={formData.channel} onValueChange={(v) => updateField('channel', v)}>
                  <SelectTrigger className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]" data-testid="template-channel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111111] border-[#2A2A2A]">
            <CardHeader>
              <CardTitle className="text-[#F2F2F2] text-base font-['EB_Garamond']">Message Content</CardTitle>
              <p className="text-xs text-[#6F6F6F]">Use {'{variable_name}'} for dynamic content</p>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Highlight overlay */}
                <pre
                  ref={highlightRef}
                  className="absolute inset-0 overflow-hidden rounded-[12px] border border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)] p-3 font-mono text-sm leading-6 text-transparent pointer-events-none whitespace-pre-wrap break-words"
                  aria-hidden="true"
                >
                  {renderHighlightedBody()}
                </pre>
                {/* Textarea */}
                <Textarea
                  ref={textareaRef}
                  value={formData.template_body}
                  onChange={(e) => updateField('template_body', e.target.value)}
                  onScroll={handleScroll}
                  placeholder="Hi {customer_name}, join us for {event_name} on {date}!"
                  className="relative bg-transparent p-3 font-mono text-sm leading-6 text-[#F2F2F2] caret-[#D4A843] outline-none min-h-[220px] resize-none"
                  style={{ caretColor: '#D4A843' }}
                  data-testid="template-editor-body-textarea"
                />
              </div>
              {detectedVars.length > 0 && (
                <div className="mt-3 p-3 bg-[rgba(212,168,67,0.08)] border border-[rgba(212,168,67,0.18)] rounded-lg">
                  <p className="text-xs text-[#B9B9B9] mb-2">Detected Variables:</p>
                  <div className="flex flex-wrap gap-2">
                    {detectedVars.map(v => (
                      <span key={v} className="inline-block px-2 py-1 rounded bg-[rgba(212,168,67,0.14)] border border-[rgba(212,168,67,0.28)] text-xs font-mono text-[#D4A843]">
                        {'{' + v + '}'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Preview */}
        <div className="space-y-6">
          <Card className="bg-[#111111] border-[#2A2A2A] sticky top-6">
            <CardHeader>
              <CardTitle className="text-[#F2F2F2] text-base font-['EB_Garamond'] flex items-center gap-2">
                <Eye size={16} />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs defaultValue="preview" className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-[rgba(255,255,255,0.05)]">
                  <TabsTrigger value="preview">Rendered</TabsTrigger>
                  <TabsTrigger value="sample">Sample Data</TabsTrigger>
                </TabsList>
                <TabsContent value="preview" className="space-y-3">
                  <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.12)] rounded-lg min-h-[200px]">
                    <p className="text-sm text-[#F2F2F2] whitespace-pre-wrap">{preview || 'Your message will appear here...'}</p>
                  </div>
                  <div className="text-xs text-[#6F6F6F]">
                    Character count: {preview.length} / 160 (1 SMS)
                  </div>
                </TabsContent>
                <TabsContent value="sample" className="space-y-3">
                  <p className="text-xs text-[#B9B9B9]">Edit sample values to test your template</p>
                  {detectedVars.length === 0 ? (
                    <p className="text-xs text-[#6F6F6F] py-4">No variables detected yet. Add {'{variable_name}'} to your template.</p>
                  ) : (
                    <div className="space-y-3">
                      {detectedVars.map(v => (
                        <div key={v} className="space-y-1.5">
                          <Label className="text-[#B9B9B9] text-xs font-mono">{'{' + v + '}'}</Label>
                          <Input
                            value={sampleData[v] || ''}
                            onChange={(e) => setSampleData({ ...sampleData, [v]: e.target.value })}
                            placeholder={`Sample ${v}`}
                            className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2] text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}
